import { SupportAssistant } from "./SupportAssistant";
import { useShopInbox } from "./shopInbox";
import type { Locale, Workspace } from "@salon-pos/shared";
import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTlT } from "../i18n/m/team-levels";
import { api, isOnline, onOnlineChange } from "../api";
import { IS_NATIVE_BUILD } from "../native/platform";
import { ServerLongPress } from "../native/ServerSetting";
import { AuthImage } from "./AuthImage";
import { useAuth } from "../auth";
import { LOCALE_LABELS, useI18n } from "../i18n";
import { MODE_SWITCH, NATIVE_LONG_LABEL, WS_HOME, WS_LABEL, allowedWorkspaces, feedbackPath, lockedNav, nativeHome, nativeTabsFor, showAddonsEntry, visibleNav } from "../nav";
import { setHqBranch, setOwnerMode, useHqBranch, useOwnerMode } from "../lib/ownerMode";
import { canSeeFeedback, useFeedbackBadge } from "../lib/feedback";
import "./feedback-badge.css";
import { hapticTap } from "../native/device";
import { GroupedNav, NavLinkItem } from "./SideNav";
import { NavIcon } from "./NavIcon";
import { useApprovalsBadge } from "./approvalsBadge";
import { errorText, useToast } from "../ui";
import { KioskGate, KioskMenuItems } from "./Kiosk";
import { BillingBanner } from "./BillingBanner";
import { StorageBanner } from "./StorageBanner";
import { CashierBanner } from "./Cashier";
import { useTabBarHidden } from "./TabBar";
import { MustChangePassword } from "./MustChangePassword";
import { SenviaIcon } from "./SenviaLogo";
import { GuideButton } from "./Guide";

/** (Task P) first-run setup: compiled into the native build only. */
const AppSetupGate = IS_NATIVE_BUILD ? lazy(() => import("../native/SetupWizard").then((m) => ({ default: m.AppSetupGate }))) : () => null;

/** Connectivity (native: Capacitor Network; browser: online/offline events) — see api.ts. */
export function useOnline() {
  const [online, setOnline] = useState(isOnline());
  useEffect(() => onOnlineChange(setOnline), []);
  return online;
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("") || "?";
}

/** Brand block: the salon's logo (if uploaded) and name; without a salon (platform admin) the Senvia POS S mark. */
export function Brand({ name, logoUrl, product }: { name: string; logoUrl?: string | null; product?: boolean }) {
  return (
    <div className="brand">
      <ServerLongPress>{logoUrl ? <AuthImage className="brand-logo" src={logoUrl} alt="" /> : product ? <SenviaIcon size={30} className="brand-senvia" /> : <span className="brand-mark" aria-hidden>{initials(name).slice(0, 1)}</span>}</ServerLongPress>
      <span className="brand-name">{name}</span>
    </div>
  );
}

function UserMenu({ ws }: { ws: Workspace }) {
  const { me, switchTo, logout } = useAuth();
  const { t, locale, setLocale, locales } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  // (Task NN) 投诉与建议: entry + unread badge (platform replies / status changes)
  const fbOk = canSeeFeedback(me) && ws !== "platform";
  const fbUnread = useFeedbackBadge(fbOk, me?.membership?.id ?? "");
  const loc = useLocation();
  if (!me) return null;
  const workspaces = allowedWorkspaces(me);
  return (
    <div className="user-menu" ref={ref}>
      <button className="avatar-btn" aria-haspopup="menu" aria-expanded={open} aria-label={t("common.account")} onClick={() => setOpen((v) => !v)}>
        <span className="avatar">{initials(me.user.displayName)}</span>
        {fbUnread > 0 && <span className="fb-dot" aria-hidden data-testid="fb-avatar-dot" />}
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-head">
            <strong>{me.user.displayName}</strong>
            <span className="muted small">{me.user.email}</span>
            {me.membership && <span className="muted small">{t(("team.role." + me.membership.role) as never)} · {me.membership.tenantName}</span>}
          </div>
          <div className="menu-section">
            <div className="menu-label">{t("common.language")}</div>
            {locales.map((l) => (
              <button key={l} role="menuitemradio" aria-checked={locale === l} className={"menu-item" + (locale === l ? " checked" : "")} onClick={() => setLocale(l as Locale)}>{LOCALE_LABELS[l as Locale]}</button>
            ))}
          </div>
          {me.memberships.length > 1 && (
            <div className="menu-section">
              <div className="menu-label">{t("shell.switchSalon")}</div>
              {me.memberships.map((m) => (
                <button key={m.id} role="menuitemradio" aria-checked={m.id === me.membership?.id} className={"menu-item" + (m.id === me.membership?.id ? " checked" : "")}
                  onClick={() => { setOpen(false); void switchTo({ membershipId: m.id }).then(() => navigate("/")); }}>{m.tenantName}</button>
              ))}
            </div>
          )}
          {!IS_NATIVE_BUILD && workspaces.length > 1 && (
            <div className="menu-section">
              <div className="menu-label">{t("shell.switchWorkspace")}</div>
              {workspaces.map((w) => (
                <button key={w} role="menuitemradio" aria-checked={w === ws} className={"menu-item" + (w === ws ? " checked" : "")} onClick={() => { setOpen(false); navigate(WS_HOME[w]); }}>{t(WS_LABEL[w])}</button>
              ))}
            </div>
          )}
          <div className="menu-section">
            {fbOk && <button role="menuitem" className="menu-item" data-testid="menu-feedback" onClick={() => { setOpen(false); navigate(feedbackPath(ws) + "?from=" + encodeURIComponent(loc.pathname)); }}>
              {t("nn.nav")}{fbUnread > 0 && <span className="nav-badge" aria-label={t("nn.unread")}>{fbUnread}</span>}</button>}
            {IS_NATIVE_BUILD && <button role="menuitem" className="menu-item" onClick={() => { setOpen(false); navigate("/checkout/device"); }}>{t("to.device.title")}</button>}
            {ws !== "owner" && <KioskMenuItems onDone={() => setOpen(false)} />}
            <button role="menuitem" className="menu-item" onClick={() => void logout().then(() => navigate("/login"))}>{t("common.signOut")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * (Task O/P) Native store app shell (Android / iOS / iPad): only the store sections, as ONE bottom tab bar on every
 * size (max 5 tabs; extra sections under "More"), respecting the safe areas / Android gesture bar. No side rail, no
 * owner portal, billing banner or AI helper. The first-run setup (per device + member) opens over it.
 */
export function NativeShell({ children }: { children: ReactNode }) {
  const { me, switchTo } = useAuth();
  const { t } = useI18n();
  const online = useOnline();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [loc.pathname]);
  // (Task Q) full-screen flows hide the tab bar; its real height (tablet ≠ phone) drives the page padding
  const tabbarHidden = useTabBarHidden();
  // (Task NN) unread 投诉与建议 replies → badge on the Me tab
  const fbUnread = useFeedbackBadge(canSeeFeedback(me), me?.membership?.id ?? "");
  const navRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const set = () => { const h = el.getBoundingClientRect().height; if (h > 0) document.documentElement.style.setProperty("--tabbar-h", Math.ceil(h) + "px"); };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [me, tabbarHidden]);
  // (Task AN) owner: all-branch dashboard ("hq") or the store tabs; the dashboard's branch filter (全部分店 by default)
  const mode = useOwnerMode();
  const hqBranchId = useHqBranch();
  const navigate = useNavigate();
  if (!me) return null;
  const { tabs, more } = nativeTabsFor(me, mode);
  const isOwnerHq = me.membership?.role === "owner" && mode === "hq";
  const under = (p: string) => loc.pathname === p || loc.pathname.startsWith(p + "/");
  // the dashboard's 首页 (/checkout/hq) must not light up while a sub-tab (/checkout/hq/staff …) is open
  const tabActive = (p: string) => (p === "/checkout/hq" ? loc.pathname === p : under(p));
  const moreActive = more.some((i) => i.path !== MODE_SWITCH && under(i.path));
  const flipMode = () => {
    const next = mode === "hq" ? "store" : "hq";
    setOwnerMode(next);
    setMoreOpen(false);
    navigate(nativeHome(me), { replace: true });
  };
  return (
    <div className={"shell native-shell" + (tabbarHidden ? " tabbar-hidden" : "")} data-testid="native-shell">
      <KioskGate />
      <MustChangePassword />
      <Suspense fallback={null}><AppSetupGate /></Suspense>
      <div className="main">
        <header className="topbar native-topbar">
          <Brand name={me.membership?.tenantName ?? t("app.name")} logoUrl={me.tenant?.logoUrl} product={!me.membership} />
          <div className="topbar-controls">
            <GuideButton />
            {isOwnerHq && me.branches.length > 1 && (
              <label className="branch-switch">
                <span className="sr-only">{t("common.branch")}</span>
                <select value={hqBranchId} data-testid="hq-branch" onChange={(e) => setHqBranch(e.target.value)}>
                  <option value="">{t("an.allBranches")}</option>
                  {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
            )}
            {!isOwnerHq && me.branches.length > 1 && (
              <label className="branch-switch">
                <span className="sr-only">{t("common.branch")}</span>
                <select value={me.activeBranchId ?? ""} onChange={(e) => void switchTo({ branchId: e.target.value })}>
                  {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
            )}
            {me.branches.length === 1 && <span className="branch-chip">{me.branches[0]!.name}</span>}
            <UserMenu ws="checkout" />
          </div>
        </header>
        {!online && <div className="banner banner-warn" role="status">{t("mob.offline.banner")}</div>}
        <main className="content" id="content">{children}</main>
      </div>
      {!tabbarHidden && <nav ref={navRef} className="native-nav" aria-label={t("common.menu")} data-testid="tabbar">
        {tabs.map((i) => (
          <NavLink key={i.path} to={i.path} end={i.path === "/checkout/hq"} className={() => "native-tab" + (tabActive(i.path) ? " active" : "")} onClick={() => void hapticTap()}>
            <NavIcon name={i.icon} /><span className="native-tab-label">{t(i.label)}</span>
            {i.path === "/checkout/me" && fbUnread > 0 && <span className="fb-tab-badge" aria-label={t("nn.unread")} data-testid="me-tab-badge">{fbUnread}</span>}
          </NavLink>
        ))}
        {more.length > 0 && (
          <button type="button" className={"native-tab" + (moreActive || moreOpen ? " active" : "")} aria-haspopup="menu" aria-expanded={moreOpen} data-testid="tab-more"
            onClick={() => { void hapticTap(); setMoreOpen((v) => !v); }}>
            <NavIcon name="more" /><span className="native-tab-label">{t("tp.tab.more")}</span>
          </button>
        )}
      </nav>}
      {moreOpen && !tabbarHidden && (
        <>
          <button type="button" className="more-scrim" aria-label={t("common.close")} onClick={() => setMoreOpen(false)} />
          <div className="more-sheet" role="menu" aria-label={t("tp.more.title")} data-testid="more-sheet">
            <div className="more-grip" aria-hidden />
            {more.map((i) => i.path === MODE_SWITCH ? (
              <button key={i.path} type="button" role="menuitem" className="more-item more-mode" data-testid="owner-mode-switch" onClick={flipMode}>
                <NavIcon name={i.icon} /><span>{t(i.label)}</span><span className="more-chev" aria-hidden>›</span>
              </button>
            ) : (
              <NavLink key={i.path} role="menuitem" to={i.path} className={({ isActive }) => "more-item" + (isActive ? " active" : "")} onClick={() => setMoreOpen(false)}>
                <NavIcon name={i.icon} /><span>{t(NATIVE_LONG_LABEL[i.path] ?? i.label)}</span><span className="more-chev" aria-hidden>›</span>
              </NavLink>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function Shell({ ws, children }: { ws: Workspace; children: ReactNode }) {
  // the native build is constant-folded to the store shell (IS_NATIVE_BUILD is a build flag, never changes at runtime)
  if (IS_NATIVE_BUILD) return <NativeShell>{children}</NativeShell>;
  return <WebShell ws={ws}>{children}</WebShell>;
}

function WebShell({ ws, children }: { ws: Workspace; children: ReactNode }) {
  const { me, switchTo } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const online = useOnline();
  const [navOpen, setNavOpen] = useState(false);
  const loc = useLocation();
  const openNav = useCallback(() => setNavOpen(true), []);
  // (Task W) + Full-plan-only modules the salon doesn't have yet (locked, "Full" badge → upgrade note)
  const items = me ? [...visibleNav(me, ws), ...lockedNav(me, ws)] : [];
  // (Task C) 网上订单: unhandled-order badge + sound for the cashier / manager
  const shopBadge = useShopInbox(items.some((i) => i.path.endsWith("/online-orders")));
  // (Task L) 审批: pending refund / discount approvals for the owner / manager
  const apprBadge = useApprovalsBadge(ws === "owner" && items.some((i) => i.path === "/owner/approvals"), loc.pathname);
  const badgeOf = (i: { path: string }) => (i.path.endsWith("/online-orders") ? shopBadge : i.path === "/owner/approvals" ? apprBadge : 0);
  if (!me) return null;
  const salonName = me.membership?.tenantName ?? t("ws.platform");
  const close = () => setNavOpen(false);
  const resend = async () => {
    try { await api("/api/auth/resend-verification", { method: "POST", body: {} }); toast(t("shell.resent")); } catch (e) { toast(errorText(e), "error"); }
  };

  return (
    <div className={"shell" + (navOpen ? " nav-open" : "")}>
      <KioskGate />
      <MustChangePassword />
      <aside className="sidebar" aria-label={t("common.menu")}>
        <Brand name={salonName} logoUrl={me.tenant?.logoUrl} product={!me.membership} />
        {ws === "owner" ? (
          <GroupedNav items={items} userId={me.user.id} badgeOf={badgeOf} onPick={close} onRequestOpen={openNav}
            footer={!IS_NATIVE_BUILD && showAddonsEntry(me, ws) && (
              <NavLink to="/owner/addons" className={({ isActive }) => "nav-item nav-addons" + (isActive ? " active" : "")} onClick={close}>
                <NavIcon name="addons" /><span className="nav-label">{t("nav.addons")}</span>
              </NavLink>
            )} />
        ) : (
          <nav className="nav">
            {items.map((i) => <NavLinkItem key={i.path} item={i} onPick={close} badge={badgeOf(i)} />)}
          </nav>
        )}
        <div className="sidebar-powered" data-testid="powered-by"><SenviaIcon size={16} /><span>{t("app.name")}</span></div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="icon-btn menu-toggle" aria-label={t("common.menu")} aria-expanded={navOpen} onClick={() => setNavOpen((v) => !v)}>☰</button>
          <div className="topbar-title">{ws !== "owner" ? t(WS_LABEL[ws]) : ""}</div>
          <div className="topbar-controls">
            {ws !== "platform" && <GuideButton />}
            {me.branches.length > 1 && ws !== "platform" && (
              <label className="branch-switch">
                <span className="sr-only">{t("common.branch")}</span>
                <select value={me.activeBranchId ?? ""} onChange={(e) => void switchTo({ branchId: e.target.value })}>
                  {me.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
            )}
            {me.branches.length === 1 && ws !== "platform" && <span className="branch-chip">{me.branches[0]!.name}</span>}
            <UserMenu ws={ws} />
          </div>
        </header>
        {!online && <div className="banner banner-warn" role="status">{IS_NATIVE_BUILD ? t("mob.offline.banner") : t("common.offline")}</div>}
        <BillingBanner ws={ws} />
        <StorageBanner ws={ws} />
        <CashierBanner ws={ws} />
        {me.user.emailVerified === false && me.membership?.role === "owner" && (
          <div className="banner banner-info" role="status">
            <span>{t("shell.verifyBanner")}</span>
            <button className="btn btn-link" onClick={() => void resend()}>{t("shell.resend")}</button>
          </div>
        )}
        <main className="content" id="content">
          {me.membership?.role === "owner" && me.tenant && me.tenant.status !== "onboarding" && !(me.tenant.businessTypes ?? []).length && <IndustryBanner tenantId={me.tenant.id} />}
          {children}
        </main>
      </div>
      <SupportAssistant ws={ws} />
      {navOpen && <button className="scrim" aria-label={t("common.closeMenu")} onClick={close} />}
    </div>
  );
}

/** One-time banner for salons that never chose a business type (created before industry packs). */
function IndustryBanner({ tenantId }: { tenantId: string }) {
  const tl = useTlT();
  const key = "salon.industryBanner.dismissed." + tenantId;
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(key) === "1"; } catch { return false; } });
  if (hidden) return null;
  return (
    <div className="ind-banner" role="status" data-testid="industry-banner">
      <span>{tl("ind.banner")}</span>
      <span className="row gap">
        <Link className="btn btn-primary btn-sm" to="/settings?tab=salon#industry">{tl("ind.bannerAction")}</Link>
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => { try { localStorage.setItem(key, "1"); } catch { /* private mode */ } setHidden(true); }}>{tl("ind.dismiss")}</button>
      </span>
    </div>
  );
}
